/**
 * CustomReportFilterPanel (KAN-87)
 * Reusable filter panel for custom reports.
 * Used in both the Run panel and the template editor (Default Filters section).
 *
 * Layout (FX):
 *   Row 1: Trade Date | From | To | Maturity Date | From | To
 *   Row 2: Product (Spot, Forward, Forward Asiático) | CCY1 | CCY2 | Direction | Settlement
 *   Row 3: Our Party | Counterparty | Internal | Grouping1 | Grouping2 | Workflow States
 *
 * Layout (Commodity):
 *   Row 1: Trade Date | From | To | Maturity Date | From | To
 *   Row 2: Product (dynamic from backend) | Underlying | Venue | Direction
 *   Row 3: Our Party | Counterparty | Internal | Grouping1 | Grouping2 | Workflow States
 *
 * Fields with not-operator toggles (equals/≠): Our Party, Counterparty, CCY1, CCY2, Grouping1, Grouping2
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AssetClass, TradeFilters, FilterOptions, FilterOperator } from '../../types/customReports';
import './CustomReports.css';

interface CustomReportFilterPanelProps {
  filters: TradeFilters;
  onChange: (filters: TradeFilters) => void;
  filterOptions: FilterOptions | null;
  sourceType?: 'TRADES' | 'EVENT_LEVEL_REPORTS';
  assetClass?: AssetClass;
}

const DIRECTION_OPTIONS = ['Buy', 'Sell'];

const INTERNAL_OPTION_KEYS: Record<string, string> = {
  'null': 'customReports.filters.all',
  'true': 'customReports.filters.internalOnly',
  'false': 'customReports.filters.externalOnly',
};
const INTERNAL_OPTION_DEFAULTS: Record<string, string> = {
  'null': 'All',
  'true': 'Internal only',
  'false': 'External only',
};

/** Product type display options — "Forward Asiático" and "Forward Starting" are virtual types */
const PRODUCT_DISPLAY_OPTIONS = ['Spot', 'Forward', 'Forward Asiático', 'Forward Starting'];

// ============================================================
// Period helpers (EVENT_LEVEL_REPORTS)
// ============================================================

function computePeriodDates(
  type: 'monthly' | 'annual',
  year: number,
  month: number,
): { eventPeriodFrom: string; eventPeriodTo: string } {
  if (type === 'annual') {
    return {
      eventPeriodFrom: `${year}-01-01`,
      eventPeriodTo: `${year}-12-31`,
    };
  }
  const monthStr = String(month).padStart(2, '0');
  const lastDay = new Date(year, month, 0).getDate();
  return {
    eventPeriodFrom: `${year}-${monthStr}-01`,
    eventPeriodTo: `${year}-${monthStr}-${String(lastDay).padStart(2, '0')}`,
  };
}

function initPeriodStateFromFilters(eventPeriodFrom: string | null): {
  type: 'monthly' | 'annual';
  year: number;
  month: number;
} {
  if (eventPeriodFrom) {
    const d = new Date(eventPeriodFrom + 'T00:00:00');
    const isAnnual = d.getMonth() === 0 && d.getDate() === 1;
    return { type: isAnnual ? 'annual' : 'monthly', year: d.getFullYear(), month: d.getMonth() + 1 };
  }
  // No period set — return 0 so dropdowns show blank placeholder
  return { type: 'monthly', year: 0, month: 0 };
}

const MONTH_INDEXES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

/** All defined event types for EVENT_LEVEL_REPORTS source type */
const EVENT_TYPE_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'New Trade',         labelKey: 'customReports.eventTypes.newTrade' },
  { value: 'Expired',           labelKey: 'customReports.eventTypes.expired' },
  { value: 'Roll-Over',         labelKey: 'customReports.eventTypes.rollOver' },
  { value: 'Modified',          labelKey: 'customReports.eventTypes.modified' },
  { value: 'Cancelled',         labelKey: 'customReports.eventTypes.cancelled' },
  { value: 'Early Termination', labelKey: 'customReports.eventTypes.earlyTermination' },
];

const CustomReportFilterPanel: React.FC<CustomReportFilterPanelProps> = ({
  filters,
  onChange,
  filterOptions,
  sourceType = 'TRADES',
  assetClass = 'FX',
}) => {
  const { t } = useTranslation();

  // Local state for period selector (EVENT_LEVEL_REPORTS mode only)
  const initPeriod = initPeriodStateFromFilters(filters.eventPeriodFrom);
  const [periodType, setPeriodType] = useState<'monthly' | 'annual'>(initPeriod.type);
  const [periodYear, setPeriodYear] = useState<number>(initPeriod.year);
  const [periodMonth, setPeriodMonth] = useState<number>(initPeriod.month);

  const isCommodity = assetClass === 'COMMODITY';

  // Product type options: FX uses hardcoded list, Commodity uses dynamic values from backend
  const productTypeOptions = isCommodity
    ? (filterOptions?.productTypes || [])
    : PRODUCT_DISPLAY_OPTIONS;

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

  const update = (partial: Partial<TradeFilters>) => {
    onChange({ ...filters, ...partial });
  };

  const toggleArrayItem = (field: keyof TradeFilters, value: string) => {
    const arr = (filters[field] as string[]) || [];
    const updated = arr.includes(value)
      ? arr.filter(v => v !== value)
      : [...arr, value];
    update({ [field]: updated });
  };

  /** Renders a small =/≠ toggle before a filter's value control */
  const renderNotToggle = (operatorField: keyof TradeFilters, valueField: keyof TradeFilters) => {
    const op = filters[operatorField] as FilterOperator;
    const hasValue = Array.isArray(filters[valueField])
      ? (filters[valueField] as string[]).length > 0
      : filters[valueField] != null;
    return (
      <select
        className="cr-not-toggle"
        value={op}
        onChange={(e) => update({ [operatorField]: e.target.value as FilterOperator })}
        disabled={!hasValue}
      >
        <option value="equals">{t('customReports.filters.is', 'is')}</option>
        <option value="not_equals">{t('customReports.filters.isNot', 'is not')}</option>
      </select>
    );
  };

  return (
    <div className="cr-filter-panel">
      {/* Row 1a: Period selector — Event Level Reports only */}
      {sourceType === 'EVENT_LEVEL_REPORTS' && (
        <div className="cr-filter-row">
          <div className="cr-filter-field cr-filter-label-only">
            <label>{t('customReports.filters.reportPeriod', 'Report Period')}</label>
          </div>
          <div className="cr-filter-field cr-filter-label-only">
            <div className="cr-level-toggle" style={{ marginBottom: 0 }}>
              <button
                type="button"
                className={`cr-level-btn${periodType === 'monthly' ? ' active' : ''}`}
                onClick={() => {
                  setPeriodType('monthly');
                  if (periodYear > 0 && periodMonth > 0) {
                    update(computePeriodDates('monthly', periodYear, periodMonth));
                  }
                }}
              >
                {t('customReports.filters.periodMonthly', 'Monthly')}
              </button>
              <button
                type="button"
                className={`cr-level-btn${periodType === 'annual' ? ' active' : ''}`}
                onClick={() => {
                  setPeriodType('annual');
                  if (periodYear > 0) {
                    update(computePeriodDates('annual', periodYear, periodMonth));
                  }
                }}
              >
                {t('customReports.filters.periodAnnual', 'Annual')}
              </button>
            </div>
          </div>
          <div className="cr-filter-field">
            <label>{t('customReports.filters.year', 'Year')}</label>
            <select
              value={periodYear}
              onChange={(e) => {
                const y = Number(e.target.value);
                setPeriodYear(y);
                if (y === 0) {
                  // Cleared — reset period to null
                  setPeriodMonth(0);
                  update({ eventPeriodFrom: null, eventPeriodTo: null });
                } else {
                  const m = periodMonth > 0 ? periodMonth : 1;
                  setPeriodMonth(m);
                  update(computePeriodDates(periodType, y, m));
                }
              }}
            >
              <option value={0}>—</option>
              {yearOptions.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          {periodType === 'monthly' && periodYear > 0 && (
            <div className="cr-filter-field">
              <label>{t('customReports.filters.month', 'Month')}</label>
              <select
                value={periodMonth}
                onChange={(e) => {
                  const m = Number(e.target.value);
                  setPeriodMonth(m);
                  if (m === 0) {
                    update({ eventPeriodFrom: null, eventPeriodTo: null });
                  } else {
                    update(computePeriodDates('monthly', periodYear, m));
                  }
                }}
              >
                <option value={0}>—</option>
                {MONTH_INDEXES.map(m => (
                  <option key={m} value={m}>{t(`customReports.filters.months.${m}`)}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Row 1b-event: Event Types — Event Level Reports only */}
      {sourceType === 'EVENT_LEVEL_REPORTS' && (
        <div className="cr-filter-row">
          <div className="cr-filter-field" style={{ flex: 3 }}>
            <label>{t('customReports.filters.eventTypes', 'Event Types')}</label>
            <div className="cr-checkbox-group">
              {EVENT_TYPE_OPTIONS.map(({ value, labelKey }) => (
                <label key={value} className="cr-checkbox-label">
                  <input
                    type="checkbox"
                    checked={filters.eventTypes.includes(value)}
                    onChange={() => toggleArrayItem('eventTypes', value)}
                  />
                  {t(labelKey, value)}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Row 1c: Trade Date From/To | Maturity Date From/To — Trade level only */}
      {sourceType !== 'EVENT_LEVEL_REPORTS' && (
        <div className="cr-filter-row">
          <div className="cr-filter-field cr-filter-label-only">
            <label>{t('customReports.filters.tradeDate', 'Trade Date')}</label>
          </div>
          <div className="cr-filter-field">
            <label>{t('customReports.filters.from', 'From')}</label>
            <input
              type="date"
              value={filters.tradeDateFrom || ''}
              onChange={(e) => update({ tradeDateFrom: e.target.value || null })}
            />
          </div>
          <div className="cr-filter-field">
            <label>{t('customReports.filters.to', 'To')}</label>
            <input
              type="date"
              value={filters.tradeDateTo || ''}
              onChange={(e) => update({ tradeDateTo: e.target.value || null })}
            />
          </div>
          <div className="cr-filter-field cr-filter-label-only">
            <label>{t('customReports.filters.maturityDate', 'Maturity Date')}</label>
          </div>
          <div className="cr-filter-field">
            <label>{t('customReports.filters.from', 'From')}</label>
            <input
              type="date"
              value={filters.maturityDateFrom || ''}
              onChange={(e) => update({ maturityDateFrom: e.target.value || null })}
            />
          </div>
          <div className="cr-filter-field">
            <label>{t('customReports.filters.to', 'To')}</label>
            <input
              type="date"
              value={filters.maturityDateTo || ''}
              onChange={(e) => update({ maturityDateTo: e.target.value || null })}
            />
          </div>
        </div>
      )}

      {/* Row 2: Product Type + asset-class-specific filters */}
      <div className="cr-filter-row">
        <div className="cr-filter-field">
          <label>{t('customReports.filters.productType', 'Product Type')}</label>
          <div className="cr-checkbox-group">
            {productTypeOptions.map(pt => (
              <label key={pt} className="cr-checkbox-label">
                <input
                  type="checkbox"
                  checked={filters.productTypes.includes(pt)}
                  onChange={() => toggleArrayItem('productTypes', pt)}
                />
                {pt}
              </label>
            ))}
          </div>
        </div>
        {/* FX-only: Currency 1, Currency 2, Settlement Type */}
        {!isCommodity && (
          <>
            <div className="cr-filter-field">
              <label>{t('customReports.filters.currency', 'Currency')}</label>
              <div className="cr-not-field">
                {renderNotToggle('currency1Operator', 'currency1')}
                <input
                  type="text"
                  placeholder="USD"
                  value={filters.currency1 || ''}
                  onChange={(e) => update({ currency1: e.target.value || null })}
                  maxLength={3}
                />
              </div>
            </div>
            <div className="cr-filter-field">
              <label>{t('customReports.filters.currency2', 'Currency 2')}</label>
              <div className="cr-not-field">
                {renderNotToggle('currency2Operator', 'currency2')}
                <input
                  type="text"
                  placeholder="CLP"
                  value={filters.currency2 || ''}
                  onChange={(e) => update({ currency2: e.target.value || null })}
                  maxLength={3}
                />
              </div>
            </div>
          </>
        )}
        {/* Commodity-only: Underlying, Venue */}
        {isCommodity && (
          <>
            <div className="cr-filter-field">
              <label>{t('customReports.filters.underlying', 'Underlying')}</label>
              <select
                value={filters.underlyings?.length === 1 ? filters.underlyings[0] : ''}
                onChange={(e) => update({ underlyings: e.target.value ? [e.target.value] : [] })}
              >
                <option value="">{t('customReports.filters.all', 'All')}</option>
                {(filterOptions?.underlyings || []).map(u => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>
            <div className="cr-filter-field">
              <label>{t('customReports.filters.venue', 'Venue')}</label>
              <select
                value={filters.venues?.length === 1 ? filters.venues[0] : ''}
                onChange={(e) => update({ venues: e.target.value ? [e.target.value] : [] })}
              >
                <option value="">{t('customReports.filters.all', 'All')}</option>
                {(filterOptions?.venues || []).map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
          </>
        )}
        <div className="cr-filter-field">
          <label>{t('customReports.filters.direction', 'Direction')}</label>
          <select
            value={filters.directions.length === 1 ? filters.directions[0] : ''}
            onChange={(e) => update({ directions: e.target.value ? [e.target.value] : [] })}
          >
            <option value="">{t('customReports.filters.all', 'All')}</option>
            {DIRECTION_OPTIONS.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
        {/* FX-only: Settlement Type */}
        {!isCommodity && (
          <div className="cr-filter-field">
            <label>{t('customReports.filters.settlement', 'Settlement')}</label>
            <select
              value={filters.settlementTypes.length === 1 ? filters.settlementTypes[0] : ''}
              onChange={(e) => update({ settlementTypes: e.target.value ? [e.target.value] : [] })}
            >
              <option value="">{t('customReports.filters.all', 'All')}</option>
              <option value="ENTREGA_FISICA">Entrega Física</option>
              <option value="COMPENSACION">Compensación</option>
            </select>
          </div>
        )}
      </div>

      {/* Row 3: Our Party | Counterparty | Internal | Grouping1 | Grouping2 | Workflow States */}
      <div className="cr-filter-row">
        <div className="cr-filter-field">
          <label>{t('customReports.filters.ourParty', 'Our Party')}</label>
          <div className="cr-not-field">
            {renderNotToggle('ourPartyOperator', 'ourParty')}
            <select
              value={filters.ourParty || ''}
              onChange={(e) => update({ ourParty: e.target.value || null })}
            >
              <option value="">{t('customReports.filters.all', 'All')}</option>
              {(filterOptions?.ourPartyValues || []).map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="cr-filter-field">
          <label>{t('customReports.filters.counterparty', 'Counterparty')}</label>
          <div className="cr-not-field">
            {renderNotToggle('counterpartyOperator', 'counterparties')}
            <select
              value=""
              onChange={(e) => {
                if (e.target.value && !filters.counterparties.includes(e.target.value)) {
                  update({ counterparties: [...filters.counterparties, e.target.value] });
                }
              }}
            >
              <option value="">{filters.counterparties.length === 0 ? t('customReports.filters.all', 'All') : `${filters.counterparties.length} selected`}</option>
              {(filterOptions?.counterparties || []).filter(c => !filters.counterparties.includes(c)).map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          {filters.counterparties.length > 0 && (
            <div className="cr-selected-tags">
              {filters.counterparties.map(c => (
                <span key={c} className="cr-tag">
                  {c}
                  <button onClick={() => update({ counterparties: filters.counterparties.filter(x => x !== c) })}>&times;</button>
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="cr-filter-field">
          <label>{t('customReports.filters.internal', 'Internal')}</label>
          <select
            value={filters.isInternalTrade === null ? 'null' : String(filters.isInternalTrade)}
            onChange={(e) => update({ isInternalTrade: e.target.value === 'null' ? null : e.target.value === 'true' })}
          >
            {['null', 'true', 'false'].map(val => (
              <option key={val} value={val}>{t(INTERNAL_OPTION_KEYS[val], INTERNAL_OPTION_DEFAULTS[val])}</option>
            ))}
          </select>
        </div>
        <div className="cr-filter-field">
          <label>{filterOptions?.grouping1Label || 'Grouping 1'}</label>
          <div className="cr-not-field">
            {renderNotToggle('grouping1Operator', 'grouping1')}
            <select
              value={filters.grouping1 || ''}
              onChange={(e) => update({ grouping1: e.target.value || null })}
            >
              <option value="">{t('customReports.filters.all', 'All')}</option>
              {(filterOptions?.grouping1Values || []).map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="cr-filter-field">
          <label>{filterOptions?.grouping2Label || 'Grouping 2'}</label>
          <div className="cr-not-field">
            {renderNotToggle('grouping2Operator', 'grouping2')}
            <select
              value={filters.grouping2 || ''}
              onChange={(e) => update({ grouping2: e.target.value || null })}
            >
              <option value="">{t('customReports.filters.all', 'All')}</option>
              {(filterOptions?.grouping2Values || []).map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
        </div>
        {filterOptions?.grouping3Label && (
          <div className="cr-filter-field">
            <label>{filterOptions.grouping3Label}</label>
            <div className="cr-not-field">
              {renderNotToggle('grouping3Operator', 'grouping3')}
              <select
                value={filters.grouping3 || ''}
                onChange={(e) => update({ grouping3: e.target.value || null })}
              >
                <option value="">{t('customReports.filters.all', 'All')}</option>
                {(filterOptions?.grouping3Values || []).map(v => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        <div className="cr-filter-field" style={{ flex: 2 }}>
          <label>{t('customReports.filters.workflowStates', 'Workflow States')}</label>
          <div className="cr-checkbox-group">
            {(filterOptions?.workflowStates || ['Draft', 'Booked', 'Confirmed', 'ContractSigned', 'Final', 'Cancelled', 'Unwound']).map(ws => (
              <label key={ws} className="cr-checkbox-label">
                <input
                  type="checkbox"
                  checked={filters.workflowStates.includes(ws)}
                  onChange={() => toggleArrayItem('workflowStates', ws)}
                />
                {ws}
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomReportFilterPanel;
